// Mock backend: a fake server behind the client's transport (and a fake WebSocket), so screens can be built and
// screenshotted without the Worker. Enable with ?mock=1 (sticky for the tab) or VITE_MOCK=1. ?mock=0 turns it off.
// Accounts live in sessionStorage. Sign in with any email + password "xiaoman" for the day-9 demo (Margo;
// add ?ch=tavo etc. to the URL for another buddy). Invite codes: anything like XM-ABCD except XM-0000.
// Response shapes, error codes and chat event orders follow server/README.md (checked against wrangler dev, Nov 1):
// chat `user, delta*, message+, end`; crisis `user, crisis, message, message(crisis), end`; free limit
// `limit, message(limit), end` (no user echo, not stored). Say "I want to end my life" to see the crisis path.
import type {
  AppState, Card, CharId, ChatEvent, DoneFrom, DoneNoteData, LaterWhen, Message, Reward, RouteStop, StartOfferData, StuckAction, StuckChatData, StuckHelp, StuckReason, WorkCheckData,
} from '@shared/types';
import { WORK_SAID, workKeepText } from '@shared/content/work';
import { CHAR_IDS } from '@shared/types';
import { LATER_CHIPS, LINES, STUCK_REASON_LABEL, fill } from '@shared/content/lines';
import {
  STUCK_CHAT, STUCK_SAID, doneNoteText, firstStepCanned, stuckAlt, stuckHelpFor, stuckOutLater, stuckOutSplit, stuckOutStart,
} from '@shared/content/stuck';
import { C } from '@shared/content/characters';
import {
  POINTS, addDays, atLocal, bonusesFor, dayKey, canOpenStop, chaptersOfStops, dateLabel, flameGrewAt, fmtTime, isBigReward, isPerfect,
  keepsakesOfStops, laterTarget, levelOf, localMinutes, nextFlameIn, parseTime, soundsOfStops, stageOfStops, taskShort,
} from '@shared/rules';
import type { AppEvent, AppEventKind, FirstHelpRequest, FirstHelpResponse, OnboardingStep, RegisterBody } from './types';
import { setTransport } from './client';
import { setSocketFactory, type LiveSocket } from './live';
import { sseEncode } from './sse';
import type { FirstStage } from '@shared/types';
import { ALMOST_REPLY, NEXT_REPLY, OB_HELLO_STAGE, PICKUP_REPLY } from '@shared/content/lines';
import {
  DEMO_INVITE, DEMO_NOW, DEMO_PASSWORD, PLAN, SPLIT, USER_DAY, USER_THING, buddyState, dayZeroState, demoState, mkCard, msg, proposed,
} from './fixtures';
import { routerStore } from '../router';
import { say } from '../lib/say';

const SS = 'xm.mock';
const LATENCY = 350;

interface Account {
  password: string; state: AppState; offset: number; splits?: Record<string, Card>;
  /** The last card finished (like the server's kv 'lastDone'): Undo puts `before` back. */
  lastDone?: { cardId: string; day: string; reward: Reward; before: Card } | null;
}
interface Db { accounts: Record<string, Account>; tokens: Record<string, string> }

function load(): Db {
  try { const d = JSON.parse(sessionStorage.getItem(SS) || 'null'); if (d && d.accounts) return d as Db; } catch { /* ignore */ }
  return { accounts: {}, tokens: {} };
}
let db: Db = load();
const save = () => { try { sessionStorage.setItem(SS, JSON.stringify(db)); } catch { /* ignore */ } };

/** Is mock mode on? (?mock=1 sticks for the tab; VITE_MOCK=1 at build/dev time.) */
export function mockEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const q = new URLSearchParams(location.search).get('mock');
  try {
    if (q === '1') sessionStorage.setItem('xm.mockOn', '1');
    if (q === '0') sessionStorage.removeItem('xm.mockOn');
    if (sessionStorage.getItem('xm.mockOn') === '1') return true;
  } catch { if (q === '1') return true; }
  return import.meta.env.VITE_MOCK === '1' || import.meta.env.VITE_MOCK === 'true';
}

// ------------------------------------------------------------------ helpers
class HttpError extends Error { constructor(public status: number, public code: string, message: string) { super(message); } }
const json = (body: unknown, status = 200) => new Response(JSON.stringify(body), { status, headers: { 'Content-Type': 'application/json' } });
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T;

function account(req: { headers: Headers }): { email: string; acc: Account } {
  const auth = req.headers.get('Authorization') || '';
  const token = auth.replace(/^Bearer\s+/i, '');
  const email = db.tokens[token];
  const acc = email ? db.accounts[email] : undefined;
  if (!email || !acc) throw new HttpError(401, 'unauthorized', 'Please sign in again.');
  return { email, acc };
}
const nowMs = (acc: Account) => Date.now() + acc.offset;

function commit(acc: Account, st: AppState): AppState {
  syncStuck(st);
  st.v = (acc.state.v || 0) + 1;
  st.now = new Date(nowMs(acc)).toISOString();
  acc.state = st;
  save();
  for (const s of sockets) s.push({ t: 'state', state: clone(st) });
  return clone(st);
}

const validInvite = (code: string) => /^XM-[A-Z0-9]{4}$/i.test(code.trim()) && code.trim().toUpperCase() !== 'XM-0000';

function refreshBuddy(st: AppState) {
  const b = st.buddy;
  b.stage = stageOfStops(b.stops); b.chaptersOpen = chaptersOfStops(b.stops);
  b.keepsakes = keepsakesOfStops(st.ch, b.stops); b.sounds = soundsOfStops(b.stops);
  const fresh = buddyState(st.ch, { stops: b.stops, level: st.level.level, daysTogether: b.daysTogether });
  b.route = fresh.route;
  st.buddies[st.ch] = { met: true, daysTogether: b.daysTogether, stops: b.stops, stage: b.stage };
}

const sortCards = (cards: Card[]) => cards.sort((a, b) => a.at - b.at || a.order - b.order);
const findCard = (st: AppState, id: string) => {
  const c = st.plan.cards.find((x) => x.id === id);
  if (!c) throw new HttpError(404, 'no_card', 'That card is gone.');
  return c;
};

// ------------------------------------------------------------------ onboarding content (demo)
const REASON_PREP: Record<'place' | 'late' | 'need', string> = {
  place: "Write the first sentence in your phone's notes.",
  late: 'Write the first sentence in your notes. Then sleep.',
  need: 'In your notes, list what you need for the report.',
};
/** Already under way: the step for now is about what's left (server fake.ts, same wording). */
const REASON_PREP_LEFT: Record<'place' | 'late' | 'need', string> = {
  place: "In your phone's notes, list what's left for the Q3 report.",
  late: "List what's left for the Q3 report in your notes. Then sleep.",
  need: 'In your notes, list what you need to finish the Q3 report.',
};
/** Tiny steps for a card that must differ from the one turned down (server fake.ts TINY). */
const TINY = ['Just get it where you can see it. That counts.', "Say the very first move out loud. That's it.", 'Look at it for ten seconds. Then you can stop.'];

/** Where they are with it, like the server's fake mode: their answer (`where`), "Not quite" in words = unknown (no
 *  words are read), else what the O8 opening line asked (it follows the O3 answer). */
function mockStage(body: FirstHelpRequest): FirstStage {
  if ('where' in body && body.where) return body.where;
  if ('fix' in body && body.fix) return 'unknown';
  return OB_HELLO_STAGE[body.ob?.stuck || ''] || 'new';
}

/** The thing, like the server's fake mode: what they said (first sentence, fillers off), else the demo report. */
const FILLER = /^(and|also|then|plus|maybe|please|oh|ok|okay|so|um|uh|i\s+(really\s+)?(want|need|have|got|would like|'d like|should|must)\s+to|i\s+wanna|i\s+gotta|gotta|need\s+to|have\s+to|want\s+to|finish|finally|do\s+the|do|get)\s+/i;
const VERBS = /^(call|email|text|write|finish|start|clean|pay|book|send|fix|file|buy|do|make|read|reply|schedule|submit|cancel|renew|update|review|prepare|plan|practice|study|apply|return|pick|get|go|take|wash|cook|order|check|clear|sort|tidy|organize|organise|fill|sign)\b/i;
function thingFrom(text: string): string {
  const k = String(text || '').trim().split(/[.!?\n]/)[0].replace(FILLER, '').trim();
  return (k.charAt(0).toUpperCase() + k.slice(1)).slice(0, 48) || 'The Q3 report';
}

function firstHelp(body: FirstHelpRequest): FirstHelpResponse {
  if (!validInvite(body.invite || '')) throw new HttpError(403, 'invite_invalid', 'That invite code no longer works.');
  const said = 'text' in body && body.text ? body.text : 'audio' in body && body.audio ? USER_THING : '';
  const thing = 'thing' in body && body.thing ? body.thing : thingFrom(said);
  // The demo report keeps the SPEC wording; any other thing gets the fake server's generic steps.
  const report = /\bq3\b/i.test(thing);
  const short = taskShort(thing);
  const lower = /^[A-Z][a-z]/.test(short) ? short.charAt(0).toLowerCase() + short.slice(1) : short;
  const long = short.length > 36 || (!VERBS.test(lower) && (short.length > 28 || short.split(/\s+/).length > 4));
  const O = report ? 'the Q3 report' : long ? 'it' : lower;
  const verb = !report && !long && VERBS.test(O);
  // Lines filled like the server fills them ({Task} = the thing), so a line that gains a token still reads right here.
  const L0 = LINES[body.ch];
  const t = (x: string) => say(x, { n: C[body.ch].name, task: thing });
  const L = { firstReply: t(L0.firstReply), firstCard: t(L0.firstCard), firstSmaller: t(L0.firstSmaller), none: t(L0.none), prep: t(L0.prep) };
  if (!report) {
    L.firstCard = verb ? `${short}: just the first tiny step.` : `Open ${O}. Do one tiny piece.`;
    L.firstSmaller = verb ? `${short}: just ten seconds of it.` : `Just open ${O}. That counts.`;
  }
  const stage = mockStage(body);
  const started = stage === 'middle' || stage === 'almost';
  // The step by stage (server fake.ts), and another of the same kind for "Not quite".
  const BY: Record<FirstStage, { reply: string; card: string; smaller: string; alt?: [string, string] }> = {
    new: { reply: L.firstReply, card: L.firstCard, smaller: L.firstSmaller },
    none: { reply: L.firstReply, card: L.firstCard, smaller: L.firstSmaller },
    middle: verb
      ? { reply: PICKUP_REPLY[body.ch], card: `${short}: write down what's left.`, smaller: `${short}: look at where you stopped.`, alt: [`${short}: find the last thing you did.`, `${short}: look at where you stopped.`] }
      : { reply: PICKUP_REPLY[body.ch], card: `Open ${O}. Write down what's left.`, smaller: `Just open ${O}. That counts.`, alt: [`Open ${O}. Find the last thing you finished.`, `Just open ${O} and scroll to the end.`] },
    almost: verb
      ? { reply: ALMOST_REPLY[body.ch], card: `${short}: list the last few things left.`, smaller: `${short}: look at the last bit.`, alt: [`${short}: do the smallest thing left.`, `${short}: pick the smallest thing left.`] }
      : { reply: ALMOST_REPLY[body.ch], card: `Open ${O}. List the last few things left.`, smaller: `Just open ${O} and look at the end.`, alt: [`Open ${O}. Do the smallest thing left.`, `Just open ${O}. Pick the smallest thing.`] },
    unknown: { reply: NEXT_REPLY[body.ch], card: verb ? `${short}: write down the next step.` : `Write down the next thing ${O} needs.`, smaller: `Just open ${O}. That counts.` },
  };
  const step = BY[stage];
  let { card, smaller } = step;
  // "Not quite" never hands back the card they turned down, nor one from an earlier round.
  const fix = 'card' in body && ('where' in body || 'fix' in body) ? body : null;
  if (fix) {
    const avoid = [fix.card, ...(fix.turnedDown || [])];
    const ok = (x?: string): x is string => !!x && !avoid.includes(x);
    if (!ok(card)) {
      if (step.alt && ok(step.alt[0])) [card, smaller] = step.alt;
      else card = [smaller, step.alt?.[1], ...TINY].find(ok) || TINY[0];
    }
    if (smaller === card) smaller = TINY.find((x) => x !== card) || TINY[0];
  }
  const base: FirstHelpResponse = { reply: step.reply, card, smaller, thing, said, stage };
  if ('smaller' in body && body.smaller) {
    const tiny = report ? 'Just find the report and open it. Nothing else.' : TINY.find((x) => x !== body.card) || TINY[0];
    return { ...base, reply: 'Smaller is fine. Just that.', card: tiny, smaller: tiny };
  }
  if ('reason' in body && body.reason) {
    const tomorrow = { time: '9:30 AM', at: 570, title: `${report ? 'Q3 report' : short}: ${started ? 'pick up' : 'start'} from your notes`, min: 25 };
    if (body.reason === 'none') {
      // Like the server: no `tomorrow` here; the picked time goes on a card titled with `thing`.
      return {
        ...base, reply: L.none,
        pickTime: { line: L.none, card: verb ? `Pick a time to ${lower}.` : `Pick a time for ${O}.`, options: [
          { label: 'Tomorrow 9:30 AM', at: 570, day: 'tomorrow' },
          { label: 'Tomorrow after lunch', at: 780, day: 'tomorrow' },
          { label: 'Tonight 8:00 PM', at: 1200, day: 'today' },
        ] },
      };
    }
    // prep.label is the NOW card header (as the server sends it); the done-card note comes from the app.
    const prepCard = report ? (started ? REASON_PREP_LEFT : REASON_PREP)[body.reason] : started
      ? { place: `In your phone's notes, list what's left for ${O}.`, late: `List what's left for ${O} in your notes. Then sleep.`, need: `In your notes, list what you need to finish ${O}.` }[body.reason]
      : { place: `In your phone's notes, write the first step for ${O}.`, late: `Write the first step for ${O} in your notes. Then sleep.`, need: `In your notes, list what you need for ${O}.` }[body.reason];
    return { ...base, reply: L.prep, prep: { line: L.prep, card: prepCard, label: 'NOW · 2 MIN' }, tomorrow };
  }
  return base;
}

// ------------------------------------------------------------------ cards
/** Like engine.stuck (shared/src/content/stuck.ts): a big card of 15+ minutes gets small cards (the report the demo
 *  split), a small one a first step; call cards get the call help. */
function stuckHelp(st: AppState, card: Card, reason: StuckReason): StuckHelp {
  if (reason === 'big' && card.min >= 15) {
    let at = card.at;
    const generic = [['get it out and ready', 2], ['do the first part', 5], ['do one more part', Math.max(4, card.min - 7)]] as const;
    const split = card.id === 'r' ? SPLIT.map(proposed)
      : generic.map(([t, min]) => { const p = proposed({ at, title: `${taskShort(card.title)}: ${t}`, min }); at += 5; return p; });
    return stuckHelpFor(st.ch, card.title, reason, { split });
  }
  if (reason === 'big' || reason === 'start') return stuckHelpFor(st.ch, card.title, reason, { firstStep: firstStepCanned(card.title, reason) });
  return stuckHelpFor(st.ch, card.title, reason);
}

// Stuck in chat (server: core/stuck.ts). Open stuck messages close when their card is no longer to do (commit).
const sdata = (m: Message) => m.data as StuckChatData;
const openStuckOf = (st: AppState) => st.messages.filter((m) => m.kind === 'stuck' && sdata(m)?.status === 'open');
function syncStuck(st: AppState) {
  for (const m of openStuckOf(st)) {
    const c = st.plan.cards.find((x) => x.id === sdata(m).cardId);
    if (!c || c.st !== 'todo') m.data = { ...m.data, status: 'closed' };
  }
}
function mockAsk(st: AppState, c: Card, said: string, iso: string): Message {
  const you = msg(st.ch, 'user', 'text', said, iso);
  const data: StuckChatData = { cardId: c.id, title: c.title, step: 'ask', reason: null, status: 'open', said: you.id };
  const ask = msg(st.ch, 'buddy', 'stuck', STUCK_CHAT[st.ch].ask, iso, data);
  st.messages.push(you, ask);
  return ask;
}

function done(acc: Account, id: string, from: DoneFrom | null = null): { state: AppState; reward: Reward } {
  if (from != null && from !== 'chat' && from !== 'work-chat') throw new HttpError(400, 'bad_request', "from must be 'chat' or 'work-chat'.");
  const st = clone(acc.state);
  const tz = st.user.tz;
  const card = findCard(st, id);
  if (card.st === 'done') {
    // A double tap gets the same reward (like the server), also after the light celebration cleared it.
    if (st.reward && st.reward.id === card.id) return { state: clone(acc.state), reward: st.reward };
    if (acc.lastDone && acc.lastDone.cardId === card.id) return { state: clone(acc.state), reward: acc.lastDone.reward };
    throw new HttpError(409, 'already_done', 'That card is already done.');
  }
  if (card.st !== 'todo') throw new HttpError(409, 'conflict', "This card is not on today's list.");
  settleWork(st, card.id, 'done'); settleStart(st, card.id, 'closed');
  const before = clone(card);
  const direct = !card.startedAt;
  const now = nowMs(acc);
  const litBefore = st.streak.litToday;
  // Like the server: ticked off without Start, "on time" and "back to back" count from the tick.
  const startedAt = card.startedAt ? Date.parse(card.startedAt) : now;
  const prev = st.plan.cards.filter((c) => c.st === 'done' && c.doneAt).map((c) => Date.parse(c.doneAt!));
  const bonus = bonusesFor({ litBefore, startedAt, cardAt: atLocal(card.day, card.at, tz), usedStuck: !!card.stuck, prevDoneAt: prev.length ? Math.max(...prev) : null });
  const gained = POINTS.base + bonus.reduce((a, b) => a + b.pts, 0);
  card.st = 'done'; card.doneAt = new Date(now).toISOString();
  const perfect = !st.plan.perfectGiven && isPerfect(st.plan.cards);
  if (perfect) st.plan.perfectGiven = true;
  const pointsBefore = st.points;
  const pointsAfter = pointsBefore + gained + (perfect ? POINTS.perfect : 0);
  const levelFrom = st.level.level;
  st.points = pointsAfter; st.level = levelOf(pointsAfter);
  let unlock: RouteStop | null = null;
  for (let L = levelFrom + 1; L <= st.level.level; L++) {
    if (canOpenStop(st.buddy.stops)) { unlock = st.buddy.route[st.buddy.stops] || null; st.buddy.stops += 1; }
  }
  const streakFrom = st.streak.count;
  if (!litBefore) {
    st.streak.count += 1; st.streak.litToday = true; st.streak.best = Math.max(st.streak.best, st.streak.count);
    st.streak.week = st.streak.week.map((d) => (d.day === st.today ? { ...d, mark: 'lit' } : d));
    st.buddy.daysTogether += 1;
  }
  refreshBuddy(st);
  if (unlock) unlock = st.buddy.route[unlock.idx];
  const live = st.plan.cards.filter((c) => c.st !== 'removed');
  const L = LINES[st.ch];
  const reward: Reward = {
    id: card.id, title: card.alt || card.title, time: fmtTime(localMinutes(now, tz)), doneAt: card.doneAt!, base: POINTS.base, bonus, gained,
    minutes: Math.max(1, Math.round((now - startedAt) / 60000)),
    ...(direct ? { direct: true } : {}),
    dayDone: live.filter((c) => c.st === 'done').length, dayTotal: live.length,
    streakFrom, streakTo: litBefore ? null : st.streak.count,
    flameGrew: !litBefore && flameGrewAt(st.streak.count), nextFlameIn: nextFlameIn(st.streak.count),
    perfect, levelFrom, levelTo: st.level.level, unlock, pointsBefore, pointsAfter,
    line: fill(L.signature, { Task: taskShort(card.title), year: st.today.slice(0, 4) }),
  };
  st.reward = reward;
  st.collection.cardsDone += 1;
  st.collection.recent = [{ id: card.id, title: card.title, day: card.day, doneAt: card.doneAt! }, ...st.collection.recent].slice(0, 20);
  if (from) {
    const data: DoneNoteData = { cardId: card.id, title: card.title, gained, how: from === 'work-chat' ? 'work' : 'tick', line: reward.line, rewardId: reward.id };
    st.messages.push(msg(st.ch, 'system', 'done', doneNoteText(st.ch, data), card.doneAt!, data));
  }
  acc.lastDone = { cardId: card.id, day: st.today, reward, before };
  return { state: commit(acc, st), reward };
}

/** Later on a card inside `st` (Later sheet, a time chip on a stuck help) -> the card id (tomorrow's copy for 'Tomorrow'). */
function laterOn(acc: Account, st: AppState, id: string, when: LaterWhen): string {
  const card = findCard(st, id);
  const tgt = laterTarget(when, localMinutes(nowMs(acc), st.user.tz));
  if (!tgt.tomorrow) { card.at = tgt.at; card.time = fmtTime(tgt.at); sortCards(st.plan.cards); return card.id; }
  card.st = 'moved'; card.movedTo = addDays(st.today, 1);
  const cardId = card.id + '_t';
  st.tomorrow.push(mkCard(card.movedTo, { id: cardId, at: tgt.at, title: card.title, min: card.min, source: 'carry' }));
  return cardId;
}
function later(acc: Account, id: string, when: LaterWhen): { state: AppState; cardId: string } {
  const st = clone(acc.state);
  const cardId = laterOn(acc, st, id, when);
  return { state: commit(acc, st), cardId };
}

/** Small cards replace card `id` inside `st` (Stuck split): the report gets the demo ids r1..r3. */
function splitOn(acc: Account, st: AppState, id: string, list: { title: string; min: number }[]): Card[] {
  const c = findCard(st, id);
  let at = c.at;
  const kids = list.map((k, i) => {
    const card = mkCard(st.today, { id: (id === 'r' && SPLIT[i]?.id) || id + '_' + (i + 1), at, title: k.title, min: k.min, from: id, source: 'stuck' }, c.order);
    at += k.min > 5 ? 10 : 5;
    return card;
  });
  st.plan.cards = sortCards(st.plan.cards.filter((x) => x.id !== id).concat(kids));
  acc.splits = { ...(acc.splits || {}), [id]: c };
  return kids;
}

/** Crisis words (a small subset of the server's list in server/src/core/crisis.ts). */
const CRISIS = /\b(kill myself|end my life|suicid\w*|want to die|self[- ]harm|hurt myself)\b/i;

// ------------------------------------------------------------------ chat (SSE)
function chatStream(acc: Account, body: { text?: string; audio?: unknown; image?: unknown }): Response {
  const st = clone(acc.state);
  const ch = st.ch;
  const iso = new Date(nowMs(acc)).toISOString();
  const text = body.text || (body.audio ? 'Okay, sounds good.' : body.image ? '' : '');
  const L0 = LINES[ch];
  const crisis = CRISIS.test(text);
  if (!crisis && !st.user.vip && st.usage.messagesToday >= st.usage.freeLimit) {
    // Free limit: the user's text is not stored; it comes back in the limit message (data.text) for the input.
    const lim = msg(ch, 'system', 'limit', L0.vipLine, iso, { text, voice: !!body.audio, limit: st.usage.freeLimit });
    st.messages.push(lim);
    return sseResponse([{ t: 'limit' }, { t: 'message', message: lim }, { t: 'end', state: commit(acc, st) }]);
  }
  const user: Message = { ...msg(ch, 'user', 'text', text, iso), voice: !!body.audio, image: body.image ? '/assets/' + ch + '/key.jpg' : null };
  const events: ChatEvent[] = [{ t: 'user', message: user }];
  st.messages.push(user);
  if (crisis) {
    const a = msg(ch, 'buddy', 'text', L0.crisisLine, iso);
    const b = msg(ch, 'system', 'crisis', '', iso, { source: 'keywords' });
    st.messages.push(a, b);
    events.push({ t: 'crisis' }, { t: 'message', message: a }, { t: 'message', message: b }, { t: 'end', state: commit(acc, st) });
    return sseResponse(events);
  }
  st.usage.messagesToday += 1;
  // Morning ask answered -> the plan proposal (final text, then the plan card); otherwise the change question.
  const proposing = st.plan.status === 'asked' || st.plan.status === 'none';
  // Chat tool complete_card ("I already called the dentist"): the same path as Done, so end.state.reward is set.
  const finished = !proposing && /\b(already|just)\b.*\b(did|done|finished|paid|called|sent|booked|made)\b|\b(did|finished|paid|called) (it|the)\b/i.test(text)
    ? st.plan.cards.find((c) => c.st === 'todo' && taskShort(c.title).toLowerCase().split(/\W+/).some((w) => w.length >= 4 && text.toLowerCase().includes(w))) || null
    : null;
  if (finished) {
    const line = say(L0.done[0], { n: C[ch].name, task: finished.title });
    for (const word of line.split(/(?<= )/)) events.push({ t: 'delta', text: word });
    const dm = msg(ch, 'buddy', 'text', line, iso);
    st.messages.push(dm);
    events.push({ t: 'message', message: dm });
    commit(acc, st);
    events.push({ t: 'end', state: done(acc, finished.id).state });
    return sseResponse(events);
  }
  const reply = proposing ? say(L0.propose, { n: C[ch].name, task: PLAN[0].title }) : L0.changeAsk;
  for (const word of reply.split(/(?<= )/)) events.push({ t: 'delta', text: word });
  const rm = msg(ch, 'buddy', 'text', reply, iso);
  st.messages.push(rm);
  events.push({ t: 'message', message: rm });
  if (proposing) {
    st.plan.status = 'proposed';
    const planMsg = msg(ch, 'buddy', 'plan', '', iso, { cards: PLAN.map(proposed), status: 'pending' });
    st.messages.push(planMsg);
    events.push({ t: 'message', message: planMsg });
  }
  events.push({ t: 'end', state: commit(acc, st) });
  return sseResponse(events);
}

function sseResponse(events: ChatEvent[]): Response {
  const enc = new TextEncoder();
  let i = 0;
  const stream = new ReadableStream<Uint8Array>({
    async pull(ctrl) {
      if (i >= events.length) { ctrl.close(); return; }
      await sleep(events[i].t === 'delta' ? 40 : 120);
      ctrl.enqueue(enc.encode(sseEncode([events[i++]])));
    },
  });
  return new Response(stream, { status: 200, headers: { 'Content-Type': 'text/event-stream' } });
}

// ------------------------------------------------------------------ router
type Handler = (req: { body: any; headers: Headers; m: RegExpMatchArray; path?: string }) => Promise<Response> | Response;
const routes: [string, RegExp, Handler][] = [];
const on = (method: string, re: RegExp, h: Handler) => routes.push([method, re, h]);

on('POST', /^\/api\/invite\/check$/, ({ body }) => json({ ok: validInvite(String(body.code || '')) }));
on('POST', /^\/api\/onboarding\/first-help$/, async ({ body }) => { await sleep(700); return json(firstHelp(body as FirstHelpRequest)); });
on('POST', /^\/api\/stt\/public$/, async ({ body }) => {
  if (!validInvite(String(body.invite || ''))) throw new HttpError(403, 'invite_invalid', 'That invite code no longer works.');
  await sleep(500);
  if (body.hint === 'feedback') return json({ text: 'I tapped Start and nothing happened.' });
  if (body.hint === 'notquite') return json({ text: "I've already started it. I'm about halfway through." });
  const change = body.hint ? body.hint === 'change' : routerStore.get().s === 'O2';
  return json({ text: change ? 'I want to stop being late with work stuff.' : USER_THING });
});
// Admin events (ADMIN_P0 §2), kept in memory (xmMock.events / xmMock.steps) so tests can look. Like the server: events
// need an account, at most 50 per call, unknown kinds dropped; steps are gated by the invite like first-help (except
// o1_view, before there is one), and register links this device's steps to the account (uid).
const EVENT_KINDS = { app_open: 1, app_bg: 1, push_open: 1, cowork_end: 1, film_mode: 1, share_card: 1 } satisfies Record<AppEventKind, 1>;
const OB_STEPS = {
  o1_view: 1, o1_ok: 1, o2: 1, o3: 1, o4: 1, o5: 1, o6_ok: 1, o6_change: 1, o7: 1, o8_card: 1, o8_accept: 1, o8_smaller: 1, o8_notquite: 1,
  o8_cantnow: 1, o9_start: 1, o9_done: 1, o10_lit: 1, o10_time: 1, o11_saved: 1,
} satisfies Record<OnboardingStep, 1>;
const mockEvents: (AppEvent & { uid: string })[] = [];
const mockSteps: { invite: string; device: string; step: OnboardingStep; data: unknown; uid: string | null }[] = [];
on('POST', /^\/api\/events$/, (r) => {
  const { email } = account(r);
  const list: unknown[] = Array.isArray(r.body.events) ? r.body.events : [];
  if (list.length > 50) throw new HttpError(400, 'too_many', 'At most 50 events per call.');
  const ok = list.filter((e): e is AppEvent => !!e && typeof e === 'object' && (e as AppEvent).kind in EVENT_KINDS);
  mockEvents.push(...ok.map((e) => ({ ...e, uid: email })));
  return json({ ok: true, saved: ok.length });
});
on('POST', /^\/api\/onboarding\/step$/, ({ body }) => {
  const step = String(body.step || '') as OnboardingStep;
  if (!(step in OB_STEPS)) throw new HttpError(400, 'bad_request', 'Unknown step.');
  if (step !== 'o1_view' && !validInvite(String(body.invite || ''))) throw new HttpError(403, 'invite_invalid', 'That invite code no longer works.');
  mockSteps.push({ invite: String(body.invite || ''), device: String(body.device || ''), step, data: body.data ?? null, uid: null });
  return json({ ok: true });
});

on('POST', /^\/api\/auth\/register$/, ({ body }) => {
  const r = body as RegisterBody;
  const email = String(r.email || '').trim().toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new HttpError(400, 'bad_email', 'That email looks off.');
  if (String(r.password || '').length < 6) throw new HttpError(400, 'bad_password', 'Use at least 6 characters for the password.');
  if (db.accounts[email]) throw new HttpError(409, 'email_taken', 'That email already has an account. Sign in instead.');
  if (!validInvite(r.invite || '')) throw new HttpError(403, 'invite_invalid', 'That invite code no longer works.');
  if (r.device) for (const x of mockSteps) if (x.device === r.device && !x.uid) x.uid = email;
  const state = dayZeroState({ ...r, email }, Date.now());
  db.accounts[email] = { password: r.password, state, offset: 0 };
  const token = 'mock.' + Math.random().toString(36).slice(2);
  db.tokens[token] = email; save();
  return json({ token, state: clone(state) });
});
on('POST', /^\/api\/auth\/login$/, ({ body }) => {
  const email = String(body.email || '').trim().toLowerCase();
  const pw = String(body.password || '');
  let acc = db.accounts[email];
  if (!acc && pw === DEMO_PASSWORD && email) {
    const q = new URLSearchParams(location.search).get('ch');
    const ch: CharId = (CHAR_IDS as string[]).includes(q || '') ? (q as CharId) : 'margo';
    const state = demoState(ch, 'planned');
    state.user.email = email;
    acc = db.accounts[email] = { password: pw, state, offset: Date.parse(DEMO_NOW) - Date.now() };
  }
  if (!acc || acc.password !== pw) throw new HttpError(401, 'bad_login', "That email and password don't match.");
  const token = 'mock.' + Math.random().toString(36).slice(2);
  db.tokens[token] = email; save();
  return json({ token, state: clone(acc.state) });
});
on('POST', /^\/api\/auth\/logout$/, ({ headers }) => {
  const t = (headers.get('Authorization') || '').replace(/^Bearer\s+/i, '');
  delete db.tokens[t]; save();
  return json({ ok: true });
});
/** Drop every token of `email`; open sockets close with 4001 (signed out) or 4002 (account deleted). */
function endSessions(email: string, code: 4001 | 4002) {
  for (const [t, e] of Object.entries(db.tokens)) if (e === email) delete db.tokens[t];
  save();
  for (const s of [...sockets]) if (s.email === email) s.kick(code);
}
on('POST', /^\/api\/auth\/logout-all$/, (r) => { endSessions(account(r).email, 4001); return json({ ok: true }); });
on('DELETE', /^\/api\/account$/, (r) => { const { email } = account(r); delete db.accounts[email]; endSessions(email, 4002); return json({ ok: true }); });
on('POST', /^\/api\/debug\/restart$/, (r) => { const { email } = account(r); delete db.accounts[email]; endSessions(email, 4002); return json({ ok: true, restart: true }); });
on('GET', /^\/api\/messages$/, (r) => {
  const { acc } = account(r); const all = acc.state.messages;
  const u = new URL('http://x' + (r.path || ''));
  const before = u.searchParams.get('before');
  const limit = Math.max(1, Math.min(200, Number(u.searchParams.get('limit')) || 60));
  const end = before ? Math.max(0, all.findIndex((m) => m.id === before)) : all.length;
  return json({ messages: clone(all.slice(Math.max(0, end - limit), end)) });
});
on('GET', /^\/api\/state$/, (r) => json({ state: clone(account(r).acc.state) }));
on('POST', /^\/api\/chat$/, (r) => chatStream(account(r).acc, r.body));
on('POST', /^\/api\/stt$/, async ({ body }) => {
  await sleep(400);
  const text = body.hint === 'week' ? "Opening the report doesn't feel like a big deal anymore." : body.hint === 'plan' ? USER_DAY
    : body.hint === 'feedback' ? FEEDBACK_SAID : 'Okay, sounds good.';
  return json({ text });
});
// β feedback and page errors: kept in memory (xmMock.feedback / xmMock.errors) so screenshots and tests can look.
const FEEDBACK_SAID = 'I tapped Start and nothing happened.';
const mockFeedback: unknown[] = [];
const mockErrors: unknown[] = [];
on('POST', /^\/api\/feedback$/, async ({ body }) => {
  await sleep(300);
  if (!String(body.text || '').trim() && !body.shot && !(body.photos || []).length) throw new HttpError(400, 'bad_request', 'Say or type what happened.');
  mockFeedback.push(body);
  return json({ ok: true, id: 'fb_mock' + String(mockFeedback.length).padStart(7, '0') });
});
on('POST', /^\/api\/client-errors$/, ({ body }) => { mockErrors.push(...(body.errors || [])); return json({ ok: true, saved: (body.errors || []).length }); });
on('POST', /^\/api\/plan\/confirm$/, (r) => {
  const { acc } = account(r);
  const st = clone(acc.state);
  const m = st.messages.find((x) => x.id === r.body.messageId && x.kind === 'plan');
  const cards: { at: number; title: string; min: number }[] = m?.data?.cards || PLAN;
  if (m) m.data = { ...m.data, status: 'accepted' };
  // Like the server: cards already waiting for today (carry, onboarding ...) are reused when the titles match; other
  // to-do cards not in the plan are removed, with an undoable change message right after the planned line.
  const waiting = st.plan.cards.filter((c) => c.st === 'todo' && !c.startedAt);
  const planned = cards.map((p, i) => {
    const same = waiting.find((c) => c.title === p.title);
    return same ? { ...same, at: p.at, time: fmtTime(p.at), min: p.min, order: i } : mkCard(st.today, { id: (PLAN[i] && PLAN[i].title === p.title ? PLAN[i].id : 'c' + i), at: p.at, title: p.title, min: p.min }, i);
  });
  const dropped = waiting.filter((c) => !planned.some((x) => x.id === c.id)).map((c) => ({ ...c, st: 'removed' as const }));
  st.plan.cards = sortCards(st.plan.cards.filter((c) => !waiting.includes(c)).concat(dropped, planned));
  st.plan.status = 'planned';
  const first = planned[0];
  const iso = new Date(nowMs(acc)).toISOString();
  st.messages.push(msg(st.ch, 'buddy', 'planned', say(LINES[st.ch].planned, { n: C[st.ch].name, time: first ? first.time : '' }), iso));
  if (dropped.length) {
    st.messages.push(msg(st.ch, 'buddy', 'change', '', iso, { changeId: 'nip' + st.v, items: dropped.map((c) => ({ type: 'removed', title: c.title, detail: 'Not in the plan', cardIds: [c.id] })) }));
  }
  return json({ state: commit(acc, st) });
});
on('PATCH', /^\/api\/plan\/([^/]+)$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const m = st.messages.find((x) => x.id === decodeURIComponent(r.m[1]) && x.kind === 'plan');
  if (!m) throw new HttpError(404, 'not_found', 'Plan not found.');
  if (m.data?.status !== 'pending') throw new HttpError(409, 'not_pending', 'This plan is already set. Change the cards in Today instead.');
  const cards = ((r.body.cards || []) as { time: string; title: string; min: number }[])
    .map((c) => ({ time: c.time, at: parseTime(c.time) ?? -1, title: String(c.title || '').trim(), min: Number(c.min) || 15 }))
    .filter((c) => c.title && c.at >= 0).sort((a, b) => a.at - b.at);
  if (!cards.length) throw new HttpError(400, 'bad_request', 'A plan needs at least one card.');
  m.data = { ...m.data, cards: cards.map((c) => ({ ...c, time: fmtTime(c.at) })), edited: true };
  return json({ state: commit(acc, st) });
});
on('PATCH', /^\/api\/cards\/([^/]+)$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state); const c = findCard(st, decodeURIComponent(r.m[1]));
  if (c.st !== 'todo') throw new HttpError(409, 'not_todo', 'Only cards still to do can be changed.');
  const b = r.body as { title?: string; time?: string; min?: number };
  if (b.title != null) { const t = String(b.title).trim(); if (!t) throw new HttpError(400, 'bad_request', 'A card needs a few words.'); c.title = t; }
  if (b.time != null) { const at = parseTime(String(b.time)); if (at == null) throw new HttpError(400, 'bad_request', 'time must look like 9:30 AM.'); c.at = at; c.time = fmtTime(at); }
  if (b.min != null) c.min = Math.max(1, Math.min(240, Math.round(Number(b.min) || c.min)));
  st.plan.cards = sortCards(st.plan.cards);
  return json({ state: commit(acc, st), cardId: c.id });
});
// Working together (11-05, like server/src/core/work.ts): one session at a time; her asks and offers settle with it.
function settleWork(st: AppState, id: string, status: WorkCheckData['status']) {
  for (const m of st.messages) if (m.kind === 'work' && (m.data as WorkCheckData)?.cardId === id && (m.data as WorkCheckData).status === 'pending') m.data = { ...m.data, status };
}
function settleStart(st: AppState, id: string, status: StartOfferData['status']) {
  for (const m of st.messages) if (m.kind === 'start' && (m.data as StartOfferData)?.cardId === id && (m.data as StartOfferData).status === 'pending') m.data = { ...m.data, status };
}
on('POST', /^\/api\/cards\/([^/]+)\/start$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state); const c = findCard(st, decodeURIComponent(r.m[1]));
  for (const o of st.plan.cards) if (o.id !== c.id && o.st === 'todo' && o.startedAt) { o.startedAt = null; o.alt = null; settleWork(st, o.id, 'closed'); }
  settleWork(st, c.id, 'closed'); settleStart(st, c.id, 'started');
  c.startedAt = new Date(nowMs(acc)).toISOString(); c.alt = r.body.alt ?? null;
  return json({ state: commit(acc, st) });
});
on('POST', /^\/api\/cards\/([^/]+)\/stop$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state); const c = findCard(st, decodeURIComponent(r.m[1]));
  if (c.st === 'todo' && c.startedAt) { c.startedAt = null; c.alt = null; settleWork(st, c.id, 'closed'); }
  return json({ state: commit(acc, st) });
});
on('POST', /^\/api\/work\/([^/]+)$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const m = st.messages.find((x) => x.id === decodeURIComponent(r.m[1]) && x.kind === 'work');
  if (!m) throw new HttpError(404, 'not_found', 'Not found.');
  if (r.body?.action !== 'keep') throw new HttpError(400, 'bad_request', 'Unknown action.');
  const d = m.data as WorkCheckData;
  if (d.status === 'kept') return json({ state: clone(acc.state) });
  const c = st.plan.cards.find((x) => x.id === d.cardId);
  if (d.status !== 'pending' || !c || c.st !== 'todo' || c.startedAt !== d.at) throw new HttpError(409, 'ended', 'Working together on this card already ended.');
  m.data = { ...d, status: 'kept' };
  const iso = new Date(nowMs(acc)).toISOString();
  st.messages.push(msg(st.ch, 'user', 'text', WORK_SAID.keep, iso), msg(st.ch, 'buddy', 'text', workKeepText(st.ch, d.n, d.min), iso));
  return json({ state: commit(acc, st) });
});
on('POST', /^\/api\/cards\/([^/]+)\/done$/, (r) => json(done(account(r).acc, decodeURIComponent(r.m[1]), (r.body.from ?? null) as DoneFrom | null)));
// Undo for the light celebration: only the card just finished, today, and not a big one (isBigReward).
on('POST', /^\/api\/cards\/([^/]+)\/undone$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state); const c = findCard(st, decodeURIComponent(r.m[1]));
  const ld = acc.lastDone;
  if (c.st !== 'done') throw new HttpError(409, 'not_done', 'This card is not done.');
  if (!ld || ld.cardId !== c.id) throw new HttpError(409, 'not_latest', 'Only the card you just finished can be undone.');
  if (ld.day !== st.today) throw new HttpError(409, 'too_old', 'That card was finished on another day.');
  if (isBigReward(ld.reward)) throw new HttpError(409, 'big', "This one can't be undone.");
  Object.assign(c, ld.before, { st: 'todo', doneAt: null });
  st.points -= ld.reward.gained; st.level = levelOf(st.points);
  st.collection.cardsDone = Math.max(0, st.collection.cardsDone - 1);
  st.collection.recent = st.collection.recent.filter((x) => x.id !== c.id);
  if (st.reward?.id === c.id) st.reward = null;
  for (const m of st.messages) if (m.kind === 'done' && (m.data as DoneNoteData)?.cardId === c.id && !m.data.undone) m.data = { ...m.data, undone: true };
  acc.lastDone = null;
  return json({ state: commit(acc, st) });
});
// Stuck in chat: no reason = "I'm stuck on …" + her ask; a reason = "{reason}." + her help (written into the chat).
on('POST', /^\/api\/cards\/([^/]+)\/stuck-chat$/, async (r) => {
  const reason = (r.body.reason ?? null) as StuckReason | null;
  if (reason != null && !STUCK_REASON_LABEL[reason]) throw new HttpError(400, 'bad_request', 'Unknown reason.');
  if (reason === 'big' || reason === 'start') await sleep(900); // the real help may wait for the model (about 3 s)
  const { acc } = account(r); const st = clone(acc.state); const c = findCard(st, decodeURIComponent(r.m[1]));
  if (c.st !== 'todo') throw new HttpError(409, 'not_todo', "This card is not on today's list.");
  const iso = new Date(nowMs(acc)).toISOString();
  const open = openStuckOf(st);
  if (reason == null) {
    const last = st.messages[st.messages.length - 1];
    if (last && last.kind === 'stuck' && sdata(last).status === 'open' && sdata(last).step === 'ask' && sdata(last).cardId === c.id) return json({ state: clone(acc.state), messageId: last.id });
    for (const m of open) m.data = { ...m.data, status: 'closed' };
    const ask = mockAsk(st, c, fill(STUCK_SAID.stuck, { title: c.title }), iso);
    return json({ state: commit(acc, st), messageId: ask.id });
  }
  let ask = open.find((m) => sdata(m).cardId === c.id && sdata(m).step === 'ask') || null;
  for (const m of open) if (m !== ask) m.data = { ...m.data, status: 'closed' };
  if (!ask) ask = mockAsk(st, c, fill(STUCK_SAID.stuck, { title: c.title }), iso);
  ask.data = { ...ask.data, reason, status: 'resolved' };
  c.stuck = true;
  const help = stuckHelp(st, c, reason);
  const you = msg(st.ch, 'user', 'text', fill(STUCK_SAID.reason, { label: STUCK_REASON_LABEL[reason] }), iso);
  const data: StuckChatData = { cardId: c.id, title: c.title, step: 'help', reason, help, out: null, status: 'open', act: null, said: you.id };
  const hm = msg(st.ch, 'buddy', 'stuck', help.line, iso, data);
  st.messages.push(you, hm);
  return json({ state: commit(acc, st), messageId: hm.id, help });
});
on('POST', /^\/api\/stuck\/([^/]+)$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const a = r.body as StuckAction;
  if (!['start', 'later', 'split', 'again', 'close'].includes(String(a?.action))) throw new HttpError(400, 'bad_request', 'action must be start, later, split, again or close.');
  const m = st.messages.find((x) => x.id === decodeURIComponent(r.m[1]) && x.kind === 'stuck');
  if (!m) throw new HttpError(404, 'not_found', 'That stuck message is gone.');
  const d = sdata(m);
  if (a.action === 'close') { if (d.status === 'open') m.data = { ...d, status: 'closed' }; return json({ state: commit(acc, st) }); }
  if (d.status !== 'open') {
    if (d.status === 'resolved' && d.act === 'start' && a.action === 'start') return json({ state: clone(acc.state), cardId: d.cardId });
    throw new HttpError(409, 'closed', 'This one is closed. Tap Stuck again if you need it.');
  }
  const c = st.plan.cards.find((x) => x.id === d.cardId);
  if (!c || c.st !== 'todo') throw new HttpError(409, 'not_todo', "This card is not on today's list any more.");
  const iso = new Date(nowMs(acc)).toISOString();
  if (a.action === 'again') {
    if (d.step !== 'help') throw new HttpError(400, 'bad_request', 'Something else is for a help card.');
    m.data = { ...d, status: 'closed' };
    const ask = mockAsk(st, c, STUCK_SAID.again, iso);
    return json({ state: commit(acc, st), messageId: ask.id });
  }
  const help = d.help;
  if (d.step !== 'help' || !help) throw new HttpError(400, 'bad_request', "Pick what's in the way first.");
  if (a.action === 'start') {
    c.startedAt = iso; c.alt = stuckAlt(help, d.title);
    m.data = { ...d, status: 'resolved', act: 'start', out: stuckOutStart(st.ch, help, d.title) };
    return json({ state: commit(acc, st), cardId: c.id });
  }
  if (a.action === 'later') {
    if (!(LATER_CHIPS as readonly string[]).includes(a.when)) throw new HttpError(400, 'bad_request', 'Unknown time.');
    const cardId = laterOn(acc, st, c.id, a.when);
    m.data = { ...d, status: 'resolved', act: 'later', out: stuckOutLater(d.title, a.when) };
    return json({ state: commit(acc, st), cardId });
  }
  if (help.reason !== 'big' || !help.split || help.split.length < 2) throw new HttpError(400, 'bad_request', 'This help has no small cards.');
  const kids = splitOn(acc, st, c.id, help.split.map((x) => ({ title: x.title, min: x.min })));
  m.data = { ...d, status: 'resolved', act: 'split', out: stuckOutSplit(d.title, kids.length) };
  return json({ state: commit(acc, st), cardIds: kids.map((k) => k.id) });
});
on('POST', /^\/api\/cards\/([^/]+)\/(remove|restore)$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state); const c = findCard(st, decodeURIComponent(r.m[1]));
  c.st = r.m[2] === 'remove' ? 'removed' : 'todo';
  return json({ state: commit(acc, st) });
});
on('POST', /^\/api\/cards\/([^/]+)\/later$/, (r) => json(later(account(r).acc, decodeURIComponent(r.m[1]), r.body.when)));
on('POST', /^\/api\/cards\/([^/]+)\/stuck$/, async (r) => {
  await sleep(900); // the real help is an AI call (about 3 s): long enough to see S's thinking state
  const { acc } = account(r); const st = clone(acc.state); const c = findCard(st, decodeURIComponent(r.m[1]));
  c.stuck = true;
  const help = stuckHelp(st, c, r.body.reason);
  return json({ state: commit(acc, st), help });
});
on('POST', /^\/api\/cards\/([^/]+)\/split$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const list: { title: string; min: number }[] = r.body.cards?.length ? r.body.cards : SPLIT;
  const kids = splitOn(acc, st, decodeURIComponent(r.m[1]), list);
  return json({ state: commit(acc, st), cardIds: kids.map((k) => k.id) });
});
on('POST', /^\/api\/cards\/([^/]+)\/unsplit$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const id = decodeURIComponent(r.m[1]);
  const parentId = st.plan.cards.find((x) => x.id === id)?.from || id;
  const parent = acc.splits?.[parentId] || mkCard(st.today, PLAN[0]);
  st.plan.cards = sortCards(st.plan.cards.filter((x) => x.from !== parentId).concat([parent]));
  return json({ state: commit(acc, st), cardId: parent.id });
});
on('POST', /^\/api\/changes\/([^/]+)\/undo$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const m = st.messages.find((x) => x.kind === 'change' && x.data?.changeId === decodeURIComponent(r.m[1]));
  if (m) m.data = { ...m.data, undone: true };
  for (const it of (m?.data?.items || []) as { type: string; cardIds: string[] }[]) {
    if (it.type === 'removed') for (const id of it.cardIds) { const c = st.plan.cards.find((x) => x.id === id); if (c) c.st = 'todo'; }
  }
  return json({ state: commit(acc, st) });
});
on('POST', /^\/api\/nudge\/accept$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const m = st.messages.find((x) => x.id === r.body.messageId);
  const card = m?.data?.card || { at: 645, title: 'Call the dentist', min: 5 };
  if (m) m.data = { ...m.data, status: 'accepted' };
  const id = 'n' + st.plan.cards.length;
  st.plan.cards = sortCards(st.plan.cards.concat([mkCard(st.today, { id, at: card.at, title: card.title, min: card.min, source: 'nudge' })]));
  st.plan.status = 'planned';
  return json({ state: commit(acc, st), cardId: id });
});
on('POST', /^\/api\/notify\/action$/, (r) => {
  const { acc } = account(r);
  if (r.body.action !== 'accept') return json({ state: commit(acc, clone(acc.state)) });
  const st = clone(acc.state);
  const id = 'n' + st.plan.cards.length;
  st.plan.cards = sortCards(st.plan.cards.concat([mkCard(st.today, { id, at: localMinutes(nowMs(acc), st.user.tz) + 5, title: 'Call the dentist', min: 5, source: 'nudge' })]));
  return json({ state: commit(acc, st), cardId: id });
});
on('POST', /^\/api\/offer\/([^/]+)$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const m = st.messages.find((x) => x.id === decodeURIComponent(r.m[1]));
  if (m) m.data = { ...(m.data || {}), status: r.body.accept ? 'accepted' : 'declined' };
  st.hints.offerToday = true;
  const iso = new Date(nowMs(acc)).toISOString();
  if (!r.body.accept) { st.messages.push(msg(st.ch, 'buddy', 'text', LINES[st.ch].offerNo, iso)); return json({ state: commit(acc, st), cardId: null }); }
  // Accept: a 2-minute card, already started (the app opens W).
  const id = 'o' + st.plan.cards.length;
  const next = st.plan.cards.find((c) => c.st === 'todo');
  if (m) m.data = { ...(m.data || {}), cardId: id };
  st.plan.cards = sortCards(st.plan.cards.concat([{ ...mkCard(st.today, { id, at: localMinutes(nowMs(acc), st.user.tz), title: `Two minutes: ${next ? next.title : 'anything on your list'}`, min: 2, source: 'chat' }), startedAt: iso }]));
  return json({ state: commit(acc, st), cardId: id });
});
on('POST', /^\/api\/day\/goodnight$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  st.hints.evening = false;
  st.messages.push(msg(st.ch, 'buddy', 'text', LINES[st.ch].goodnight, new Date(nowMs(acc)).toISOString()));
  return json({ state: commit(acc, st) });
});
// "Wrap up the day" from Today (server: POST /api/day/wrap writes the evening summary once). The mock's
// evening mode is derived from the cards, so it only needs to flag that the wrap is available.
on('POST', /^\/api\/day\/wrap$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  st.hints.evening = true;
  return json({ state: commit(acc, st) });
});
on('POST', /^\/api\/reward\/seen$/, (r) => { const { acc } = account(r); const st = clone(acc.state); st.reward = null; return json({ state: commit(acc, st) }); });
on('POST', /^\/api\/week\/answer$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const reply = LINES[st.ch].weekReply;
  const text = r.body.text || "Opening the report doesn't feel like a big deal anymore.";
  if (st.week) { st.week.answer = text; st.week.reply = reply; }
  return json({ state: commit(acc, st), reply, text });
});
on('POST', /^\/api\/week\/chest$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  st.points += POINTS.weekChest; st.level = levelOf(st.points);
  if (st.week) st.week.chest = 'opened';
  return json({ state: commit(acc, st), points: POINTS.weekChest });
});
on('POST', /^\/api\/story\/(\d+)\/choice$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  st.buddy.chapterChoices[r.m[1]] = String(r.body.choice || '');
  return json({ state: commit(acc, st) });
});
on('POST', /^\/api\/story\/(\d+)\/read$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  if (st.buddy.newChapter === Number(r.m[1])) st.buddy.newChapter = null;
  return json({ state: commit(acc, st) });
});
on('PATCH', /^\/api\/memory\/([^/]+)$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  st.buddy.memory = st.buddy.memory.map((x) => (x.id === decodeURIComponent(r.m[1]) ? { ...x, text: String(r.body.text || '') } : x));
  return json({ state: commit(acc, st) });
});
on('DELETE', /^\/api\/memory\/([^/]+)$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  st.buddy.memory = st.buddy.memory.filter((x) => x.id !== decodeURIComponent(r.m[1]));
  return json({ state: commit(acc, st) });
});
on('DELETE', /^\/api\/memory$/, (r) => { const { acc } = account(r); const st = clone(acc.state); st.buddy.memory = []; return json({ state: commit(acc, st) }); });
/** A new day for the user (debug Next day, or a zone where it is already tomorrow): tomorrow's cards, her morning ask. */
function startDay(acc: Account, st: AppState, day: string) {
  st.today = day; st.dateLabel = dateLabel(st.today); st.dayIndex += 1;
  st.streak.litToday = false; st.plan = { day: st.today, status: 'asked', cards: st.tomorrow.map((c, i) => ({ ...c, day: st.today, order: i })), perfectGiven: false };
  st.tomorrow = []; st.reward = null;
  st.messages.push(msg(st.ch, 'buddy', 'text', LINES[st.ch].morning, new Date(nowMs(acc)).toISOString()));
}
on('PATCH', /^\/api\/settings$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const b = r.body as { planTime?: string; checkin?: AppState['user']['checkin']; reminders?: boolean; tz?: string };
  if (b.planTime) { st.user.planTime = b.planTime; st.tomorrowPlanTime = b.planTime; }
  if (b.checkin) st.user.checkin = b.checkin;
  if (typeof b.reminders === 'boolean') st.user.reminders = b.reminders;
  // Like the server: 'auto' follows this device again at once (its X-TZ); a zone where it is already tomorrow starts
  // the new day there.
  const okTz = (z: string) => { try { new Intl.DateTimeFormat('en-US', { timeZone: z }); return true; } catch { return false; } };
  if (b.tz === 'auto') {
    st.user.tzAuto = true;
    const dev = r.headers.get('X-TZ');
    if (dev && okTz(dev)) st.user.tz = dev;
  } else if (b.tz) {
    if (!okTz(b.tz)) throw new HttpError(400, 'bad_request', "tz must be 'auto' or a time zone like America/Los_Angeles.");
    st.user.tzAuto = false; st.user.tz = b.tz;
  }
  if (b.tz) { const d = dayKey(nowMs(acc), st.user.tz); if (d > st.today) startDay(acc, st, d); }
  return json({ state: commit(acc, st) });
});
on('POST', /^\/api\/buddy\/switch$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  const ch = r.body.ch as CharId;
  if (!(CHAR_IDS as string[]).includes(ch)) throw new HttpError(400, 'bad_ch', 'Unknown buddy.');
  const prev = st.buddies[ch];
  st.ch = ch;
  st.buddy = buddyState(ch, { stops: prev?.stops || 0, level: st.level.level, daysTogether: prev?.daysTogether || 0 });
  st.buddies[ch] = { met: true, daysTogether: st.buddy.daysTogether, stops: st.buddy.stops, stage: st.buddy.stage };
  return json({ state: commit(acc, st) });
});
on('POST', /^\/api\/vip$/, (r) => { const { acc } = account(r); const st = clone(acc.state); st.user.vip = !!r.body.on; return json({ state: commit(acc, st) }); });
on('POST', /^\/api\/friends\/share$/, (r) => { const { acc } = account(r); return json({ state: commit(acc, clone(acc.state)), ok: true }); });
on('POST', /^\/api\/friends\/([^/]+)\/(nudge|cheer)$/, (r) => {
  const { acc } = account(r); const st = clone(acc.state);
  st.friends.sent[decodeURIComponent(r.m[1])] = r.m[2];
  return json({ state: commit(acc, st), ok: true });
});
on('POST', /^\/api\/devices$/, () => json({ ok: true }));
on('GET', /^\/api\/schedule$/, (r) => {
  const { acc } = account(r); const st = acc.state;
  const items = st.plan.cards.filter((c) => c.st === 'todo').map((c) => ({
    id: 'card-' + c.id, at: new Date(atLocal(c.day, c.at, st.user.tz)).toISOString(),
    title: C[st.ch].name, body: `Time for this one: ${c.title}. I'm here.`, data: { route: 'W:' + c.id },
  }));
  return json({ items });
});
on('POST', /^\/api\/debug\/([a-z0-9]+)$/, (r) => {
  const { acc } = account(r); let st = clone(acc.state);
  const tool = r.m[1];
  if (tool === 'points') { st.points += Number(r.body.add) || 100; st.level = levelOf(st.points); }
  else if (tool === 'nextday') {
    acc.offset += 86400000;
    startDay(acc, st, addDays(st.today, 1));
  } else if (tool === 'advance') acc.offset += Math.max(1, Number(r.body.minutes) || 60) * 60000;
  else if (tool === 'crisis') {
    const iso = new Date(nowMs(acc)).toISOString();
    st.messages.push(msg(st.ch, 'buddy', 'text', LINES[st.ch].crisisLine, iso), msg(st.ch, 'system', 'crisis', '', iso, { source: 'debug' }));
  } else if (tool === 'evening') st.hints.evening = true;
  else if (tool === 'away2') st.hints.back = true;
  return json({ state: commit(acc, st) });
});

async function mockTransport(url: string, init: RequestInit): Promise<Response> {
  const u = new URL(url, location.origin);
  const method = (init.method || 'GET').toUpperCase();
  let body: any = {};
  if (typeof init.body === 'string' && init.body) { try { body = JSON.parse(init.body); } catch { body = {}; } }
  const headers = new Headers(init.headers);
  await sleep(LATENCY / 2);
  if (init.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  for (const [m, re, h] of routes) {
    if (m !== method) continue;
    const match = u.pathname.match(re);
    if (!match) continue;
    try { return await h({ body, headers, m: match, path: u.pathname + u.search }); } catch (e) {
      if (e instanceof HttpError) return json({ error: { code: e.code, message: e.message } }, e.status);
      console.error('[mock]', e);
      return json({ error: { code: 'mock_error', message: String((e as Error)?.message || e) } }, 500);
    }
  }
  return json({ error: { code: 'not_found', message: `Mock has no ${method} ${u.pathname}` } }, 404);
}

// ------------------------------------------------------------------ live (fake WebSocket)
class MockSocket implements LiveSocket {
  readyState = 0;
  onopen: ((ev: unknown) => void) | null = null;
  onmessage: ((ev: { data: unknown }) => void) | null = null;
  onclose: ((ev: unknown) => void) | null = null;
  onerror: ((ev: unknown) => void) | null = null;
  email: string | null;
  constructor(url: string) {
    const token = new URL(url).searchParams.get('token') || '';
    this.email = db.tokens[token] || null;
    sockets.add(this);
    setTimeout(() => {
      if (!this.email) { this.readyState = 3; this.onclose?.({ code: 4401 }); sockets.delete(this); return; }
      this.readyState = 1; this.onopen?.({});
      const acc = db.accounts[this.email];
      if (acc) this.push({ t: 'state', state: clone(acc.state) });
    }, 60);
  }
  push(m: unknown) { if (this.readyState === 1) setTimeout(() => this.onmessage?.({ data: typeof m === 'string' ? m : JSON.stringify(m) }), 0); }
  /** hello / seen: nothing to do; the heartbeat 'ping' gets 'pong' like the server. */
  send(data: string) { if (data === 'ping') this.push('pong'); }
  /** Server-side close (4001 signed out, 4002 account deleted). */
  kick(code: number) { this.readyState = 3; sockets.delete(this); setTimeout(() => this.onclose?.({ code }), 0); }
  close() { this.readyState = 3; sockets.delete(this); setTimeout(() => this.onclose?.({ code: 1000 }), 0); }
}
const sockets = new Set<MockSocket>();

/** Swap the client's transport and socket factory for the mock. Call once at boot. */
export function installMock() {
  setTransport(mockTransport);
  setSocketFactory((url) => new MockSocket(url));
  (window as unknown as { xmMock: unknown }).xmMock = {
    reset() { db = { accounts: {}, tokens: {} }; save(); },
    db: () => db,
    invite: DEMO_INVITE,
    feedback: mockFeedback,
    errors: mockErrors,
    events: mockEvents,
    steps: mockSteps,
  };
  console.info('[xiaoman] mock backend on. Invite XM-7Q4K; sign in with any email + password "xiaoman" for the day-9 demo.');
}
